/**
 * #37141 (Mutual Services): the 360Value / RCE re-run rule for a migrated policy's dwellings (the imported term
 * and every renewal descending from it; CFM, and Forreston by Joe's decision). An endorsement re-runs 360Value
 * ONLY when the value of one of these fields changes; any other field changes nothing about the valuation. Filling in a field that came over blank
 * from the migration is data completion, not a change, so it re-runs nothing either (the term still rates on the
 * filled-in value). Applies to replacement-cost and ACV dwellings alike.
 *
 * One definition for the policy-mod trigger (farm-segment.manager) and staff-fe's confirmation pop-up, so the
 * pop-up appears exactly when the backend will re-run.
 *
 * Field names are the dwelling's `building` fields as the staff-fe forms store them. Two forms label "Home
 * Quality" differently: FO-1/2/3 and barndominium use `homeQuality`, the mobile-home form `mobileHomeQualityGrade`.
 */
export const MIGRATED_DWELLING_360_RERUN_FIELDS: readonly string[] = [
  'yearBuilt', // Year Built
  'sqFt', // Above-ground sq ft ("Total finished sqft" on a mobile home)
  'basementSqFt', // Basement sq ft
  'numFamiliesInDwelling', // Families living
  'homeQuality', // Home Quality
  'mobileHomeQualityGrade', // Home Quality (mobile home)
  'numStories', // Stories
  'constructionType' // Construction Type
];

/**
 * Listed fields where 0 is no value: a dwelling has no 0 sq ft, Year Built, stories or families, so a 0 there is a
 * blank (GO stores an unentered sq ft as 0, and the CFM import carried it over: R7 of the 10/25-pm re-test). Basement
 * sq ft is not one of them: 0 is a real "no basement". The mobile-home form stores its sq ft and Year Built in the same
 * fields.
 */
const ZERO_IS_BLANK_FIELDS: readonly string[] = ['sqFt', 'yearBuilt', 'numStories', 'numFamiliesInDwelling'];

const isBlank = (fieldName: string, value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (ZERO_IS_BLANK_FIELDS.includes(fieldName) && Number(value) === 0);

/**
 * Whether changing a migrated dwelling's `fieldName` from `oldValue` (what it carries) to `newValue` re-runs
 * 360Value: a listed field whose value changes from one value to another. A blank filled in (0 counts as blank where
 * it is no value, see ZERO_IS_BLANK_FIELDS), a cleared field or an unlisted field is not a re-run.
 */
export function isMigrated360RerunChange({
  fieldName,
  oldValue,
  newValue
}: {
  fieldName: string;
  oldValue: unknown;
  newValue: unknown;
}): boolean {
  return (
    MIGRATED_DWELLING_360_RERUN_FIELDS.includes(fieldName) &&
    !isBlank(fieldName, oldValue) &&
    !isBlank(fieldName, newValue) &&
    String(oldValue) !== String(newValue)
  );
}
